/**
 * The desktop app's download buttons (DESKTOP §2.4).
 *
 * Two pages offer them — the customer's Settings and our own console — and
 * they have to stay the same offer: the same three builds, in the same order,
 * under the same labels. So the block lives here and neither page draws its
 * own.
 *
 * The URLs come out of the feed absolute and already percent-encoded (the
 * filenames carry spaces), and the files are public, so these are plain
 * anchors: nothing to ask the API for first, and a `download` attribute the
 * browser honours itself.
 */

import type { DesktopRelease, DesktopReleaseFile } from "../api/types";
import { useI18n } from "../i18n";
import { formatBytes } from "./common";

/** Which installer to put forward. `other` — Linux, or a browser that says
 *  nothing useful — puts none forward and offers all three plainly. */
export type VisitorPlatform = "mac" | "win" | "other";

/**
 * A cosmetic guess, used only to order the buttons and to give exactly one of
 * them the primary weight. Never to *choose* a build: Apple silicon and Intel
 * are not distinguishable from a browser, so both Mac buttons are always drawn
 * — and the weight goes to Apple silicon, which every Mac sold since 2023 is.
 * An Intel Mac is the minority case, and the "which Mac" hint under the buttons
 * is what settles it. Two primary buttons would settle nothing.
 *
 * `win` is matched on the specific tokens rather than on "win", because
 * "darwin" contains that too.
 */
export function visitorPlatform(): VisitorPlatform {
  const hint = `${navigator.platform ?? ""} ${navigator.userAgent ?? ""}`.toLowerCase();
  if (hint.includes("mac")) return "mac";
  if (/windows|win32|win64/.test(hint)) return "win";
  return "other";
}

/** The three numbers of a version, or null for anything else — a build tagged
 *  `0.3.0-beta.1` compares on `0.3.0`, and a tag we cannot read at all simply
 *  never claims an update is waiting. */
function triple(version: string): [number, number, number] | null {
  const match = /^\s*(\d+)\.(\d+)\.(\d+)/.exec(version);
  return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null;
}

/** Whether the running app is behind what has been published. Unreadable on
 *  either side means "say nothing" rather than "offer an update". */
export function isOlderVersion(running: string, published: string): boolean {
  const a = triple(running);
  const b = triple(published);
  if (!a || !b) return false;
  for (let i = 0; i < 3; i += 1) {
    if (a[i] !== b[i]) return a[i] < b[i];
  }
  return false;
}

/** `kind: "zip"` is the in-app updater's own artefact — never a download. */
function installer(
  release: DesktopRelease,
  platform: "mac" | "win",
  kind: "dmg" | "exe",
  arch?: "arm64" | "x64",
): DesktopReleaseFile | undefined {
  return release.files.find(
    (file) =>
      file.platform === platform &&
      file.kind === kind &&
      (arch === undefined || file.arch === arch),
  );
}

function DownloadButton({
  file,
  label,
  primary,
  releaseVersion,
}: {
  file: DesktopReleaseFile;
  label: string;
  primary: boolean;
  releaseVersion: string;
}) {
  const { t } = useI18n();
  // A publish that only half finished leaves one platform a version behind.
  // The line above the buttons speaks for the release; this one has to speak
  // for the file, or the customer downloads a number nobody named.
  const size = formatBytes(file.size);
  const suffix =
    file.version === releaseVersion
      ? size
      : `${t("desktop.fileVersion", { version: file.version })} · ${size}`;
  return (
    <a className={primary ? "button primary" : "button"} href={file.url} download>
      {label}
      <span className="muted small">{suffix}</span>
    </a>
  );
}

export default function DesktopDownloads({ release }: { release: DesktopRelease }) {
  const { t } = useI18n();
  const macArm = installer(release, "mac", "dmg", "arm64");
  const macIntel = installer(release, "mac", "dmg", "x64");
  const windows = installer(release, "win", "exe");
  const visitor = visitorPlatform();

  const mac = (
    <>
      {macArm && (
        <DownloadButton
          file={macArm}
          label={t("desktop.downloadMacArm")}
          primary={visitor === "mac"}
          releaseVersion={release.version}
        />
      )}
      {macIntel && (
        <DownloadButton
          file={macIntel}
          label={t("desktop.downloadMacIntel")}
          primary={false}
          releaseVersion={release.version}
        />
      )}
    </>
  );
  const win = windows && (
    <DownloadButton
      file={windows}
      label={t("desktop.downloadWindows")}
      primary={visitor === "win"}
      releaseVersion={release.version}
    />
  );

  const released = release.releaseDate ? new Date(release.releaseDate) : null;
  const date = released && !Number.isNaN(released.getTime()) ? released : null;

  return (
    <>
      <p className="muted" style={{ margin: "8px 0 0" }}>
        {date
          ? t("desktop.versionLine", {
              version: release.version,
              date: date.toLocaleDateString(),
            })
          : t("desktop.versionOnly", { version: release.version })}
      </p>
      {macArm || macIntel || windows ? (
        <>
          <div className="row" style={{ margin: "8px 0 0", flexWrap: "wrap" }}>
            {/* The visitor's own platform first — the rest stay visible,
                because the computer being downloaded *for* is often not the
                one doing the downloading. */}
            {visitor === "win" ? (
              <>
                {win}
                {mac}
              </>
            ) : (
              <>
                {mac}
                {win}
              </>
            )}
          </div>
          {(macArm || macIntel) && (
            <p className="muted small" style={{ margin: "8px 0 0" }}>
              {t("desktop.whichMac")}
            </p>
          )}
        </>
      ) : (
        <p className="muted" style={{ margin: "8px 0 0" }}>
          {t("desktop.noInstallers")}
        </p>
      )}
    </>
  );
}
